import { useRef, useState } from "react";
import { PhoneNav, type PhoneBack } from "../../../shared/phone/PhoneNav";
import { PnRow, Segmented } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { usePaneTop, useScrolledPast } from "../more/useSubScroll";
import { barBack, CheckMark, CxHead, PLACES } from "./cxNative";
import { copyText, EXAMPLE_TABS, exampleFor, KEY_PLACEHOLDER, type ExampleTab, type SalesTracking } from "./useSalesTracking";

const STRIPE_KEYS = "https://dashboard.stripe.com/apikeys/create";

/** Confirming sales for partners: a read-only Stripe key, or a secret key for any other checkout. */
export function SalesDetail({ t, say, back, may }: { t: SalesTracking; say: (message: string, done?: boolean) => void; back?: PhoneBack; may: boolean }) {
  const top = useRef<HTMLDivElement>(null);
  const past = useScrolledPast(top, 110);
  usePaneTop(top, "sales");
  const [askOff, setAskOff] = useState(false);
  const [anyOpen, setAnyOpen] = useState(false);
  const p = PLACES.sales;
  const on = t.stripeConnected;

  return (
    <div className={past ? "pn-screen mo-cx-screen past" : "pn-screen mo-cx-screen"} ref={top}>
      <PhoneNav title={p.name} back={barBack(back, past, p.name)} />
      <div className="pn-stack">
        <CxHead place="sales" tone={on ? "good" : "dim"} state={on ? "Connected" : "Not connected"} stateIcon={on ? "shield" : "plug"} line="So the partner who drove a sale gets their reward. Read only, no code." on={on} />
        {t.err && <p className="mo-err mo-pad">Couldn’t load: {t.err}</p>}
        {on ? (
          <>
            <div className="pn-grp">
              <PnRow icon={<Icon name="lock" />} iconTone="plain" title="Read-only Stripe key" value={<span className="mo-key">{t.stripeKeyHint}</span>} />
              <div className="pn-row static">
                <CheckMark kind="ok" />
                <span className="m">
                  <span className="t">Listening for sales</span>
                </span>
              </div>
            </div>
          </>
        ) : (
          t.loaded &&
          may && (
            <>
              <section>
                <div className="pn-cap">1. Make a read-only key</div>
                <div className="pn-grp">
                  <PnRow title="It reads three things" sub="Checkout Sessions, Charges and Promotion codes" wrap />
                  <PnRow className="accent" icon={<Icon name="arrowOut" />} title="Open Stripe" onClick={() => window.open(STRIPE_KEYS, "_blank", "noopener")} chevron={false} />
                </div>
              </section>
              <section>
                <div className="pn-cap">2. Paste it here</div>
                <form
                  className="pn-grp mo-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void t.connectStripe();
                  }}
                >
                  <div className="pn-irow">
                    <label htmlFor="mo-rk">Key</label>
                    <input
                      id="mo-rk"
                      value={t.stripeKey}
                      onChange={(e) => {
                        t.setStripeKey(e.target.value);
                        t.setStripeErr(null);
                      }}
                      placeholder="rk_live_..."
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      enterKeyHint="go"
                      aria-label="Stripe read-only key"
                    />
                  </div>
                </form>
                {t.stripeErr ? <p className="mo-err mo-pad">{t.stripeErr}</p> : <p className="pn-note">Read only. It can never move money or change your account.</p>}
              </section>
            </>
          )
        )}
        {may && (
          <section>
            <div className="pn-cap">Not on Stripe?</div>
            <div className="pn-grp">
              <PnRow icon={<Icon name="tool" />} iconTone="plain" title="Confirm sales from any checkout" sub={t.keySet ? "Your secret key is saved" : undefined} onClick={() => setAnyOpen(true)} chevron />
            </div>
          </section>
        )}
        {on && may && (
          <div className="pn-grp">
            <PnRow danger title="Disconnect" onClick={t.stripeBusy ? undefined : () => setAskOff(true)} />
          </div>
        )}
      </div>
      {!on && t.loaded && may && (
        <div className="pn-foot">
          <button type="button" className="pn-btn pri wide" disabled={t.stripeBusy || !t.stripeKey.trim()} onClick={() => void t.connectStripe()}>
            {t.stripeBusy ? "Checking…" : "Connect"}
          </button>
        </div>
      )}
      <ActionSheet
        open={askOff}
        onClose={() => setAskOff(false)}
        title="Disconnect Stripe?"
        message="Partners stop getting credit for new sales until you connect it again."
        actions={[{ label: "Disconnect", danger: true, onSelect: () => void t.disconnectStripe() }]}
      />
      <AnyCheckout open={anyOpen} onClose={() => setAnyOpen(false)} t={t} say={say} />
    </div>
  );
}

/** The secret key a checkout posts each sale with, and the code that posts it. */
function AnyCheckout({ open, onClose, t, say }: { open: boolean; onClose: () => void; t: SalesTracking; say: (message: string, done?: boolean) => void }) {
  const [tab, setTab] = useState<ExampleTab>("curl");
  const [askRotate, setAskRotate] = useState(false);
  const code = exampleFor(tab, t.freshKey ?? KEY_PLACEHOLDER);
  const copy = async (text: string) => {
    try {
      await copyText(text);
      say("Copied to clipboard");
    } catch {
      say("Copy failed. Select and copy manually", false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      tall
      title="Any checkout"
      right={
        <button type="button" className="pn-tbtn strong" onClick={onClose}>
          Done
        </button>
      }
      className="mo-sheet"
    >
      <div className="pn-stack tight mo-any">
        <p className="mo-sub">Post each sale to Flashadz with a secret key. The order id keeps repeats from counting twice, so partner rewards stay safe from fakes.</p>
        <section>
          <div className="pn-cap">Your secret key</div>
          <div className="pn-grp">
            {t.freshKey ? (
              <>
                <div className="pn-row static">
                  <span className="m">
                    <span className="t mo-key mo-keyv fs-norecord">{t.keyRevealed ? t.freshKey : t.maskedKey}</span>
                  </span>
                </div>
                <PnRow title={t.keyRevealed ? "Hide" : "Reveal"} className="accent" onClick={() => t.setKeyRevealed((v) => !v)} chevron={false} />
                <PnRow title="Copy" className="accent" onClick={() => t.freshKey && void copy(t.freshKey)} chevron={false} />
              </>
            ) : t.keySet ? (
              <div className="pn-row static">
                <span className="m">
                  <span className="t mo-key mo-keyv">{t.maskedKey}</span>
                  <span className="s">Saved. Generate a new key to replace it.</span>
                </span>
              </div>
            ) : null}
            {t.keySet ? (
              <PnRow title="Rotate key" className="mo-warnrow" onClick={t.keyBusy ? undefined : () => setAskRotate(true)} chevron={false} />
            ) : (
              <PnRow title={t.keyBusy ? "Generating…" : "Generate key"} className="accent" icon={<Icon name="plus" />} onClick={t.keyBusy ? undefined : () => void t.generateKey(false)} chevron={false} />
            )}
          </div>
          {t.freshKey ? <p className="pn-note mo-warnnote">Copy this now. For your security, you will not be able to see it again.</p> : !t.keySet && <p className="pn-note">No key yet. Generate one to start confirming sales.</p>}
        </section>
        <section>
          <div className="pn-sh">
            <h2>Post each sale</h2>
            <button type="button" className="pn-lnk" onClick={() => void copy(code)}>
              Copy
            </button>
          </div>
          <Segmented value={tab} options={EXAMPLE_TABS.map((x) => ({ value: x.id, label: x.label }))} onChange={setTab} label="Ways to report a sale" />
          <pre className="mo-code fs-norecord" tabIndex={0}>
            {code}
          </pre>
          <p className="pn-note">Send the order id, amount, and coupon. The order id keeps repeats from counting twice.</p>
        </section>
      </div>
      <ActionSheet
        open={askRotate}
        onClose={() => setAskRotate(false)}
        title="Rotate the key?"
        message="Rotating makes a new key and stops the old one working right away. Update your backend after."
        actions={[{ label: "Rotate key", danger: true, onSelect: () => void t.generateKey(true) }]}
      />
    </Sheet>
  );
}
